import { forgetPhomymoDevice, getPhomymoAvailability, isPhomymoProfile, requestPhomymoPrinter } from './phomymoAdapter';

const STORAGE_KEY = 'klaro-printer-bindings-v1';
const SERVICE_UUID = '000018f0-0000-1000-8000-00805f9b34fb';

// Profiles describe a Bluetooth printing protocol, not a commercial printer
// model. UI and database records therefore remain generic.
export const BLUETOOTH_PROFILES = {
  tspl_ble_v1: {
    id: 'tspl_ble_v1',
    label: 'Bluetooth / TSPL',
    serviceUuid: SERVICE_UUID,
    writeCharacteristicUuid: '00002af1-0000-1000-8000-00805f9b34fb',
    notifyCharacteristicUuid: '00002af0-0000-1000-8000-00805f9b34fb',
    defaultDpi: 203,
    supportedDpi: [203],
    // This protocol does not reliably expose the loaded media dimensions.
    // Keep the hook explicit so future profiles can return detected sizes.
    mediaPresets: [],
    mediaDetection: 'manual_fallback',
  },
  phomemo_m220: {
    id: 'phomemo_m220',
    label: 'Phomemo M220',
    protocol: 'phomemo_m_series',
    serviceUuid: '0000ff00-0000-1000-8000-00805f9b34fb',
    writeCharacteristicUuid: '0000ff02-0000-1000-8000-00805f9b34fb',
    notifyCharacteristicUuid: '0000ff03-0000-1000-8000-00805f9b34fb',
    defaultDpi: 203,
    supportedDpi: [203],
    maxWidthDots: 576,
    alignment: 'right',
    namePatterns: ['M220', 'PHOMEMO M220'],
    mediaPresets: [],
    mediaDetection: 'manual_fallback',
  },
  phomemo_m221: {
    id: 'phomemo_m221',
    label: 'Phomemo M221',
    protocol: 'phomemo_m_series',
    serviceUuid: '0000ff00-0000-1000-8000-00805f9b34fb',
    writeCharacteristicUuid: '0000ff02-0000-1000-8000-00805f9b34fb',
    notifyCharacteristicUuid: '0000ff03-0000-1000-8000-00805f9b34fb',
    defaultDpi: 203,
    supportedDpi: [203],
    maxWidthDots: 576,
    alignment: 'center',
    namePatterns: ['M221', 'PHOMEMO M221'],
    mediaPresets: [],
    mediaDetection: 'manual_fallback',
  },
};

const LEGACY_PROFILE_ALIASES = {
  p1_tspl_v1: 'tspl_ble_v1',
};

const deviceCache = new Map();
const printerLocks = new Map();
const GATT_READY_DELAY_MS = 500;
const GATT_DISCONNECT_SETTLE_MS = 350;
const GATT_RETRY_DELAY_MS = 700;
const GATT_ATTEMPTS_BEFORE_WRITE = 3;

function normalizeProfileId(profileId) {
  if (!profileId) return profileId;
  return LEGACY_PROFILE_ALIASES[profileId] || profileId;
}

function profileFromDeviceName(name, profiles = Object.values(BLUETOOTH_PROFILES)) {
  const normalized = String(name || '').trim().toUpperCase();
  if (!normalized) return null;
  return profiles.find(profile => (profile.namePatterns || []).some(pattern => normalized.includes(String(pattern).toUpperCase()))) || null;
}

function bluetoothRequestError(error) {
  if (error?.name === 'NotFoundError') return new Error('Associazione Bluetooth annullata o nessuna stampante selezionata');
  if (error?.name === 'SecurityError') return new Error('Il browser ha bloccato il Bluetooth. Consenti il permesso Bluetooth e riprova.');
  if (error?.name === 'NotSupportedError') return new Error('Bluetooth non disponibile su questo dispositivo. Usa Chrome Android con Bluetooth attivo.');
  if (error?.name === 'NetworkError') return new Error('Bluetooth spento o stampante non raggiungibile. Attiva Bluetooth e avvicina la stampante.');
  return error instanceof Error ? error : new Error('Impossibile aprire la selezione Bluetooth');
}

function readBindings() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {}; }
  catch { return {}; }
}

function writeBindings(value) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  window.dispatchEvent(new CustomEvent('klaro-printer-binding-changed'));
}

export function isBluetoothSupported() {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth?.requestDevice;
}

export function getBluetoothBinding(printerId) {
  const binding = readBindings()[printerId] || null;
  if (!binding) return null;
  return { ...binding, profileId: normalizeProfileId(binding.profileId) };
}

export function bindBluetoothPrinter(printerId, device, profileId) {
  if (!printerId || !device?.id) throw new Error('Dispositivo Bluetooth non valido');
  const bindings = readBindings();
  const normalizedProfileId = normalizeProfileId(profileId);
  bindings[printerId] = {
    deviceId: device.id,
    deviceName: device.name || '',
    profileId: normalizedProfileId,
    boundAt: new Date().toISOString(),
  };
  deviceCache.set(device.id, device);
  writeBindings(bindings);
  return bindings[printerId];
}

export function removeBluetoothPrinterBinding(printerId) {
  const bindings = readBindings();
  const binding = bindings[printerId];
  if (binding?.deviceId) {
    deviceCache.delete(binding.deviceId);
    if (isPhomymoProfile(binding.profileId)) forgetPhomymoDevice(binding.deviceId).catch(() => {});
  }
  delete bindings[printerId];
  writeBindings(bindings);
}

async function grantedDevice(deviceId) {
  if (!deviceId) return null;
  if (deviceCache.has(deviceId)) return deviceCache.get(deviceId);
  if (typeof navigator?.bluetooth?.getDevices === 'function') {
    try {
      const devices = await navigator.bluetooth.getDevices();
      const found = devices.find(device => device.id === deviceId) || null;
      if (found) deviceCache.set(deviceId, found);
      return found;
    } catch { return null; }
  }
  return null;
}

const sleep = ms => new Promise(resolve => window.setTimeout(resolve, ms));

async function closeGattAndSettle(device, timeoutMs = GATT_DISCONNECT_SETTLE_MS) {
  if (!device?.gatt) return;
  try {
    if (device.gatt.connected) device.gatt.disconnect();
  } catch (_) { /* a stale GATT handle is already unusable */ }

  // Android may flip `connected` to false before the native GATT teardown has
  // actually completed. Always honour the cooldown instead of resolving as
  // soon as the JS flag changes, otherwise the next connect can fail with the
  // generic "GATT operation failed for unknown reason" error.
  if (timeoutMs > 0) await sleep(timeoutMs);
}

function reportBluetoothProgress(onProgress, message) {
  try { onProgress?.(message); } catch (_) { /* diagnostics must never break printing */ }
}

function shortUuid(uuid) {
  const match = String(uuid || '').toLowerCase().match(/^0000([0-9a-f]{4})-/);
  return match ? match[1].toUpperCase() : String(uuid || '');
}

async function openGattForDiscovery(device, { onProgress } = {}) {
  if (!device?.gatt?.connect) throw new Error('Il dispositivo non espone una connessione Bluetooth GATT');

  // Important for Android/Phomemo: reuse a valid GATT session. Calling
  // connect() again on an already connected device is unnecessary and can
  // destabilise some Android BLE stacks after a just-completed association.
  const wasConnected = Boolean(device.gatt.connected);
  reportBluetoothProgress(onProgress, wasConnected ? 'Sessione GATT già attiva · verifica servizio…' : 'Connessione GATT alla stampante…');
  const server = wasConnected ? device.gatt : await device.gatt.connect();
  if (!server?.getPrimaryService) throw new Error('Il dispositivo non espone una connessione Bluetooth GATT valida');

  // Chromium may resolve connect() before Android has completed service setup.
  // A session that was already connected has already passed this settling phase.
  if (!wasConnected) await sleep(GATT_READY_DELAY_MS);
  if (!device.gatt.connected) throw new Error('La stampante ha chiuso la connessione BLE prima della verifica del servizio');
  return server;
}

async function discoverWritableCharacteristic(device, serviceUuid, writeUuid, { onProgress } = {}) {
  let lastError;
  let lastStage = 'connessione GATT';
  let lastConnected = false;

  for (let attempt = 0; attempt < GATT_ATTEMPTS_BEFORE_WRITE; attempt += 1) {
    let stage = 'connessione GATT';
    try {
      if (attempt > 0) reportBluetoothProgress(onProgress, `Riconnessione BLE · tentativo ${attempt + 1}/${GATT_ATTEMPTS_BEFORE_WRITE}…`);
      const server = await openGattForDiscovery(device, { onProgress });

      stage = `servizio ${shortUuid(serviceUuid)}`;
      reportBluetoothProgress(onProgress, `GATT connesso · ricerca servizio ${shortUuid(serviceUuid)}…`);
      const service = await server.getPrimaryService(serviceUuid);
      if (!device.gatt?.connected) throw new Error('La stampante si è disconnessa durante la ricerca del servizio BLE');

      stage = `characteristic ${shortUuid(writeUuid)}`;
      reportBluetoothProgress(onProgress, `Servizio ${shortUuid(serviceUuid)} trovato · ricerca scrittura ${shortUuid(writeUuid)}…`);
      const characteristic = await service.getCharacteristic(writeUuid);
      if (!device.gatt?.connected) throw new Error('La stampante si è disconnessa durante la ricerca della characteristic di scrittura');

      reportBluetoothProgress(onProgress, `GATT pronto · ${shortUuid(serviceUuid)}/${shortUuid(writeUuid)} disponibili`);
      return { server, service, characteristic };
    } catch (error) {
      lastError = error;
      lastStage = stage;
      lastConnected = Boolean(device?.gatt?.connected);
      const state = lastConnected ? 'sessione ancora connessa' : 'GATT disconnesso';
      reportBluetoothProgress(onProgress, `Errore in ${stage} (${state}) · reset BLE…`);

      // Reset only after a real discovery/connect failure. This avoids the
      // unnecessary pre-emptive disconnect that was destabilising M220/M221.
      await closeGattAndSettle(device);
      if (attempt + 1 < GATT_ATTEMPTS_BEFORE_WRITE) await sleep(GATT_RETRY_DELAY_MS);
    }
  }

  const detail = lastError?.message ? ` Dettaglio: ${lastError.message}` : '';
  const disconnectHint = lastConnected ? '' : ' La sessione GATT risulta disconnessa.';
  throw new Error(`Connessione BLE fallita in ${lastStage} dopo ${GATT_ATTEMPTS_BEFORE_WRITE} tentativi.${disconnectHint}${detail} Chiudi Print Master/altre app collegate, lascia la stampante accesa e premi Riassocia.`);
}

async function verifyGattProfile(device, profile, { onProgress } = {}) {
  const { characteristic } = await discoverWritableCharacteristic(device, profile.serviceUuid, profile.writeCharacteristicUuid, { onProgress });
  const properties = characteristic?.properties || {};
  if (!properties.write && !properties.writeWithoutResponse && typeof characteristic.writeValue !== 'function') {
    // Discovery succeeded but the endpoint is not usable for printing. This is
    // the only validation failure not already cleaned up by discovery itself.
    await closeGattAndSettle(device);
    throw new Error('Il dispositivo non espone una characteristic di stampa scrivibile');
  }

  // Success deliberately leaves GATT connected. The first test print reuses
  // the session instead of forcing Android through disconnect/reconnect.
  return {
    protocol: profile.id,
    dpi: [...(profile.supportedDpi || [])],
    mediaPresets: [...(profile.mediaPresets || [])],
    mediaDetected: Boolean(profile.mediaPresets?.length),
    mediaDetection: profile.mediaDetection || 'manual_fallback',
  };
}

export async function requestBluetoothPrinter({ profileIds = null, onProgress = null } = {}) {
  if (!isBluetoothSupported()) throw new Error('Stampa Bluetooth non supportata da questo browser. Usa Chrome/Edge o Chrome Android.');
  const configuredProfiles = Array.isArray(profileIds) && profileIds.length
    ? profileIds.map(normalizeProfileId).map(id => BLUETOOTH_PROFILES[id]).filter(Boolean)
    : Object.values(BLUETOOTH_PROFILES);
  if (!configuredProfiles.length) throw new Error('Profilo Bluetooth non supportato');
  if (configuredProfiles.every(profile => isPhomymoProfile(profile.id))) {
    try { return await requestPhomymoPrinter({ profiles: configuredProfiles, onProgress }); }
    catch (error) { throw bluetoothRequestError(error); }
  }
  const profiles = Object.values(BLUETOOTH_PROFILES);
  const optionalServices = [...new Set(profiles.map(profile => profile.serviceUuid))];

  // Search is intentionally generic: the OS shows nearby Bluetooth devices and
  // Klaro validates the selected one against the protocol profiles it knows.
  let device;
  try {
    reportBluetoothProgress(onProgress, 'Seleziona la stampante Bluetooth…');
    device = await navigator.bluetooth.requestDevice({ acceptAllDevices: true, optionalServices });
    reportBluetoothProgress(onProgress, `Stampante selezionata · ${device.name || 'dispositivo Bluetooth'}`);
  } catch (error) {
    throw bluetoothRequestError(error);
  }

  let profile = null;
  let capabilities = null;
  let lastError = null;
  // M220/M221 expose the same service.  Prefer the model advertised in the
  // Bluetooth name, then validate it through GATT instead of guessing.
  const namedProfile = profileFromDeviceName(device.name, profiles);
  const candidates = [...new Map([namedProfile, ...configuredProfiles].filter(Boolean).map(candidate => [candidate.id, candidate])).values()];
  for (const candidate of candidates) {
    try {
      capabilities = await verifyGattProfile(device, candidate, { onProgress });
      profile = candidate;
      break;
    } catch (error) { lastError = error; }
  }
  if (!profile) throw new Error(`Stampante Bluetooth non ancora supportata da Klaro${lastError?.message ? `: ${lastError.message}` : ''}`);
  deviceCache.set(device.id, device);
  return {
    device,
    profileId: profile.id,
    profile,
    capabilities,
    name: device.name || 'Stampante Bluetooth',
    detectedByName: profileFromDeviceName(device.name)?.id || null,
  };
}

export async function getBluetoothAvailability(printer) {
  if (printer?.connection_type !== 'bluetooth') return { available: !!printer?.online, label: printer?.online ? 'Online' : 'Offline' };
  if (!isBluetoothSupported()) return { available: false, label: 'Bluetooth non supportato', bound: false };
  const binding = getBluetoothBinding(printer.printer_id);
  if (!printer?.verified_at) return { available: false, label: 'Da verificare in Opzioni', bound: !!binding };
  if (!binding) return { available: false, label: 'Da associare su questo dispositivo', bound: false };
  if (binding.profileId && printer.protocol_profile && normalizeProfileId(binding.profileId) !== normalizeProfileId(printer.protocol_profile)) {
    return { available: false, label: 'Da riassociare', bound: true };
  }
  if (isPhomymoProfile(binding.profileId)) return getPhomymoAvailability(binding);
  const device = await grantedDevice(binding.deviceId);
  return device
    ? { available: true, label: 'Associata su questo dispositivo', deviceName: device.name || binding.deviceName, bound: true }
    : { available: false, label: 'Da collegare su questo dispositivo', bound: true };
}

function base64Bytes(value) {
  const binary = window.atob(value || '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function writeCharacteristicChunk(characteristic, chunk, preferWriteWithResponse = false) {
  const properties = characteristic?.properties || {};
  let firstError = null;

  // For printers that support acknowledged writes, prefer them so completion
  // means the BLE peripheral accepted each chunk, not only that Android queued
  // a write-without-response operation.
  if (preferWriteWithResponse && properties.write && typeof characteristic.writeValueWithResponse === 'function') {
    await characteristic.writeValueWithResponse(chunk);
    return;
  }
  if (properties.writeWithoutResponse && typeof characteristic.writeValueWithoutResponse === 'function') {
    try { await characteristic.writeValueWithoutResponse(chunk); return; }
    catch (error) { firstError ||= error; }
  }
  if (properties.write && typeof characteristic.writeValueWithResponse === 'function') {
    try { await characteristic.writeValueWithResponse(chunk); return; }
    catch (error) { firstError ||= error; }
  }
  // Older Chromium exposes only writeValue(). Keep it as a compatibility path.
  if (typeof characteristic.writeValue === 'function') {
    try { await characteristic.writeValue(chunk); return; }
    catch (error) { firstError ||= error; }
  }
  throw firstError || new Error('Characteristic Bluetooth non scrivibile');
}

async function watchPrinterStatus(service, notifyUuid) {
  if (!notifyUuid) return { readError: () => null, stop: async () => {} };
  let notificationError = null;
  let characteristic = null;
  const onValue = event => {
    const value = new Uint8Array(event.target.value.buffer);
    // The Phomemo M-series reports paper-out as 1A 06 88.  Treat it as an
    // explicit non-success before confirming the Klaro print job.
    if (value.length >= 3 && value[0] === 0x1a && value[1] === 0x06 && value[2] === 0x88) {
      notificationError = new Error('Carta terminata o non rilevata dalla stampante Phomemo');
    }
  };
  try {
    characteristic = await service.getCharacteristic(notifyUuid);
    if (typeof characteristic.startNotifications !== 'function') return { readError: () => null, stop: async () => {} };
    await characteristic.startNotifications();
    characteristic.addEventListener('characteristicvaluechanged', onValue);
  } catch {
    // Notifications are a useful diagnostic, not a prerequisite for printing.
    characteristic = null;
  }
  return {
    readError: () => notificationError,
    stop: async () => {
      if (!characteristic) return;
      try { characteristic.removeEventListener('characteristicvaluechanged', onValue); } catch (_) { /* noop */ }
      try { await characteristic.stopNotifications?.(); } catch (_) { /* noop */ }
    },
  };
}

async function sendFramesUnlocked(printer, frames, transportConfig = {}, { onProgress = null, postSendDelayMs = null, keepGattConnected = false, preferWriteWithResponse = false } = {}) {
  const binding = getBluetoothBinding(printer.printer_id);
  if (!binding) throw new Error('Stampante da associare su questo dispositivo');
  const device = await grantedDevice(binding.deviceId);
  if (!device) throw new Error('Stampante da collegare su questo dispositivo. Apri Opzioni → Stampanti e premi Associa.');

  // `0` is a valid setting in tests and for explicitly unpaced transports;
  // use nullish defaults rather than `||`, which would silently restore the
  // generic delays.
  const chunkSize = Math.max(1, Number(transportConfig.chunk_bytes ?? 20));
  const pauseMs = Math.max(0, Number(transportConfig.chunk_pause_ms ?? 80));
  const postDelayMs = Math.max(0, Number(postSendDelayMs ?? transportConfig.post_send_delay_ms ?? 2000));
  const payloads = (frames || []).map(frame => ({
    data: frame instanceof Uint8Array ? frame : new Uint8Array(frame || []),
    pauseAfterMs: Math.max(0, Number(frame?.pauseAfterMs ?? pauseMs)),
    hasExplicitPause: Number.isFinite(Number(frame?.pauseAfterMs)),
  }));
  const totalBytes = payloads.reduce((sum, frame) => sum + frame.data.length, 0);
  let bytesWritten = 0;
  let statusWatcher = null;
  let disconnected = false;
  let sendCompleted = false;
  const onDisconnect = () => { disconnected = true; };

  try {
    const profileId = normalizeProfileId(transportConfig.profile_id || binding.profileId) || 'tspl_ble_v1';
    const profile = BLUETOOTH_PROFILES[profileId] || BLUETOOTH_PROFILES.tspl_ble_v1;
    // A retry is safe only here: no command or raster byte has been accepted.
    const { service, characteristic } = await discoverWritableCharacteristic(
      device,
      transportConfig.service_uuid || profile.serviceUuid,
      transportConfig.write_characteristic_uuid || profile.writeCharacteristicUuid,
      { onProgress },
    );
    statusWatcher = await watchPrinterStatus(service, transportConfig.notify_characteristic_uuid || profile.notifyCharacteristicUuid);
    reportBluetoothProgress(onProgress, `Invio Bluetooth · ${totalBytes} byte…`);
    device.addEventListener?.('gattserverdisconnected', onDisconnect, { once: true });
    for (let payloadIndex = 0; payloadIndex < payloads.length; payloadIndex += 1) {
      const payload = payloads[payloadIndex];
      for (let offset = 0; offset < payload.data.length; offset += chunkSize) {
        if (disconnected || !device.gatt?.connected) throw new Error('Stampante Bluetooth disconnessa durante la stampa');
        const statusError = statusWatcher.readError();
        if (statusError) throw statusError;
        const chunk = payload.data.slice(offset, Math.min(offset + chunkSize, payload.data.length));
        await writeCharacteristicChunk(characteristic, chunk, preferWriteWithResponse);
        bytesWritten += chunk.length;
        const isLastChunk = offset + chunkSize >= payload.data.length;
        const isLastPayload = payloadIndex === payloads.length - 1;
        const delay = isLastChunk ? payload.pauseAfterMs : pauseMs;
        if (delay && (!isLastPayload || !isLastChunk || payload.hasExplicitPause)) await sleep(delay);
      }
    }
    if (postDelayMs) await sleep(postDelayMs);
    const statusError = statusWatcher.readError();
    if (statusError) throw statusError;
    reportBluetoothProgress(onProgress, `Invio completato · ${bytesWritten}/${totalBytes} byte`);
    sendCompleted = true;
    return { bytesWritten, totalBytes, frameCount: payloads.length };
  } catch (error) {
    error.bytesWritten = bytesWritten;
    error.totalBytes = totalBytes;
    throw error;
  } finally {
    try { device.removeEventListener?.('gattserverdisconnected', onDisconnect); } catch (_) { /* noop */ }
    await statusWatcher?.stop();
    // Phomemo's M220 flow keeps its BLE session alive after a successful job;
    // closing GATT here can interrupt the printer's post-feed label settling.
    // Keep the existing close-and-settle behavior for other profiles and all
    // failed/partial M220 sends.
    if (!(keepGattConnected && sendCompleted)) await closeGattAndSettle(device);
  }
}

export async function sendBluetoothFrames(printer, frames, transportConfig, options = {}) {
  const key = printer?.printer_id || 'bluetooth';
  const previous = printerLocks.get(key) || Promise.resolve();
  let release;
  const current = new Promise(resolve => { release = resolve; });
  printerLocks.set(key, current);
  await previous.catch(() => {});
  try { return await sendFramesUnlocked(printer, frames, transportConfig, options); }
  finally {
    release();
    if (printerLocks.get(key) === current) printerLocks.delete(key);
  }
}

export async function sendBluetoothPayload(printer, payloadBase64, transportConfig, options = {}) {
  return sendBluetoothFrames(printer, [base64Bytes(payloadBase64)], transportConfig, options);
}
